import { useMemo } from "react";
import type { FrameRecords, SideDisplayConfig } from "../types/api";
import { allColumns, cellText, sigPosMap } from "../lib/columns";
import { sideSpans } from "../lib/columnGroups";
import { interleaveBlock } from "../lib/pairing";
import { BAND_LABEL_ATTR, useBandLabels } from "../lib/bandLabels";
import { useColumnWidths } from "./useColumnWidths";

interface Props {
  // Each side's rows, by signature. Either side may be empty — a within-ledger offset
  // has legs on one side only, and then this renders as a one-sided list under that
  // book's band (the same treatment Side-by-Side gives G1/G2).
  criFrame?: FrameRecords;
  criSigs: string[];
  criConfig?: SideDisplayConfig;
  insFrame?: FrameRecords;
  insSigs: string[];
  insConfig?: SideDisplayConfig;
  insurerName?: string;
}

// The transactions of ONE match, PAIRED: each row carries the CRI transaction on the
// left and the insurer transaction it is matched with on the right, under a spanning
// CRI | <insurer> banner.
//
// This is the system's single reading of "matched with" — the same one the Side-by-Side
// tab, the Auto-Matched tab, the Cleared-in-Subsequent-Month tab, the Prior-Period
// settled grid and the Excel paired sheets use — and the row alignment comes from the
// SAME helper (lib/pairing::interleaveBlock), not a second rule. It replaced two stacked
// single-sided tables, which made the reader carry a figure from one table's columns to
// another table's differently-ordered columns to answer the only question a match
// raises: do these two sides describe the same document?
//
// A many-to-one group ragged-fills: 3 CRI legs against 1 insurer leg render as three
// rows with the insurer band populated on the first — the shape the workbook writes.
//
// Columns come from allColumns restricted to THESE rows, so a column blank across the
// match is dropped: maximum information, no empty columns.
export default function MatchDetailTable({
  criFrame, criSigs, criConfig, insFrame, insSigs, insConfig, insurerName = "",
}: Props) {
  const resolved = useMemo(() => {
    const side = (frame: FrameRecords | undefined, sigs: string[]) => {
      if (!frame) return { pos: [] as number[], missing: sigs.length };
      const map = sigPosMap(frame);
      const pos: number[] = [];
      let missing = 0;
      for (const s of new Set(sigs)) {
        const p = map.get(s);
        if (p === undefined) missing++;
        else pos.push(p);
      }
      return { pos, missing };
    };
    return { cri: side(criFrame, criSigs), ins: side(insFrame, insSigs) };
  }, [criFrame, criSigs, insFrame, insSigs]);

  const table = useMemo(() => {
    const criCols = resolved.cri.pos.length && criFrame
      ? allColumns(criFrame, criConfig, resolved.cri.pos) : [];
    const insCols = resolved.ins.pos.length && insFrame
      ? allColumns(insFrame, insConfig, resolved.ins.pos) : [];
    const cols = [
      ...criCols.map((c) => ({ key: "cri::" + c.key, raw: c.key, label: c.label, index: c.index, ins: false })),
      ...insCols.map((c) => ({ key: "ins::" + c.key, raw: c.key, label: c.label, index: c.index, ins: true })),
    ];
    const spans = sideSpans(cols, insurerName);
    // The seam rule marks the first column of each run EXCEPT the leftmost, which
    // already has the grid border — same rule as DataTable.
    const seams = new Set(spans.slice(1).map((s) => s.firstKey));
    const rows = interleaveBlock(resolved.cri.pos, resolved.ins.pos);
    return { cols, spans, seams, rows };
  }, [resolved, criFrame, criConfig, insFrame, insConfig, insurerName]);

  // Keep each band label over the columns it names while a wide match scrolls sideways.
  const bands = useBandLabels(table.spans.length > 1, table.cols);

  const cw = useColumnWidths(useMemo(() => table.cols.map((c) => c.key), [table]), table);

  const missing = resolved.cri.missing + resolved.ins.missing;

  if (!table.cols.length) {
    return (
      <div className="muted detail-sub">
        {missing === 1 ? "This row is" : "These rows are"} not in the files uploaded this run.
      </div>
    );
  }

  return (
    <div className="min-w-0">
      <div className="detail-scroll" ref={bands.register}>
        <table className={"data-table data-table--grouped" + cw.className} style={cw.style}>
          {cw.colgroup}
          <thead>
            <tr>
              {table.spans.map((sp) => (
                <th key={sp.firstKey} colSpan={sp.span}
                  className={`side-band side-band--${sp.side}`}>
                  <span className="band-label" {...{ [BAND_LABEL_ATTR]: "" }}>{sp.label}</span>
                </th>
              ))}
            </tr>
            <tr>
              {table.cols.map((c) => (
                <th key={c.key}
                  className={"nowrap col-head" + (table.seams.has(c.key) ? " side-start-ins" : "")}>
                  {c.label}{cw.grip(c.key)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((p, i) => (
              <tr key={i}>
                {table.cols.map((c) => {
                  const pos = c.ins ? p.ins : p.cri;
                  const frame = c.ins ? insFrame : criFrame;
                  // A blank band: this side has fewer legs than the other. Rendered
                  // empty rather than skipped, so the columns stay aligned down the row.
                  const raw = pos === null || !frame ? null : frame.rows[pos][c.index];
                  const text = pos === null ? "" : cellText(raw, c.raw);
                  return (
                    <td key={c.key}
                      className={[
                        typeof raw === "number" ? "num" : "",
                        c.ins ? "side-ins" : "",
                        table.seams.has(c.key) ? "side-start-ins" : "",
                        "cell-clip",
                      ].filter(Boolean).join(" ")}
                      title={text}>
                      {text}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* A leg that isn't in this run's files is stated, never silently omitted — the
          reader would otherwise count the rows and believe the match is smaller. */}
      {missing > 0 && (
        <p className="muted detail-sub detail-missing">
          {missing} further row{missing === 1 ? "" : "s"} in this match {missing === 1 ? "is" : "are"} not
          in the files uploaded this run.
        </p>
      )}
    </div>
  );
}
